import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCurrentSession } from '../services/authService';
import { startTracking, stopTracking, setAuthToken } from '../services/locationService';
import { Button, Card, StatusBadge, Field, TextInput } from '../ui/primitives';
import { Modal } from '../ui/Modal';
import {
  IconCircleCheck,
  IconClock,
  IconNavigate,
  IconUsers,
} from '../ui/icons';
import { cx } from '../ui/cx';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

// Driver flow: a dispatcher-assigned trip arrives as a REQUEST (ASSIGNED) — the driver
// Accepts (→ CONFIRMED, fires the customer email + notifies the admin) or Declines (→ unassigned,
// back to the dispatcher's queue). Once accepted they walk the full trip lifecycle:
// Start (→ EN_ROUTE, auto-starts location tracking) → Picked up → Dropped off → Complete
// (→ COMPLETED, stops tracking + fires the trip-completed email).
const STATUS_FLOW = {
  CONFIRMED:    { label: 'Start trip',    next: 'EN_ROUTE',    variant: 'primary' },
  EN_ROUTE:     { label: 'Picked up',     next: 'PICKED_UP',   variant: 'primary' },
  PICKED_UP:    { label: 'Dropped off',   next: 'DROPPED_OFF', variant: 'primary' },
  DROPPED_OFF:  { label: 'Complete trip', next: 'COMPLETED',   variant: 'emerald' },
};

// Statuses where the driver is actively on the trip (started, not yet completed).
const ACTIVE_STATUSES = ['EN_ROUTE', 'PICKED_UP', 'DROPPED_OFF'];

// Map a Trip API response onto the shape TripCard reads. Used by both the initial
// fetch and after a status update (the PATCH returns a raw TripResponse, so without
// re-normalizing here the card would lose its status/fields and go blank).
function normalizeTrip(t) {
  // The linked booking trip carries the full itinerary + flight details (the Trip row itself
  // has none) — surfaced in the driver's trip-details popup.
  const bt = t.bookings?.[0] || {};
  return {
    assignmentId: t.tripId,
    assignmentStatus: t.tripStatus,
    bookingId: bt.bookingId,
    bookingNumber: t.tripNumber,
    customerName: bt.customerName,
    customerPhone: bt.customerPhone,
    tripSequence: 1,
    pickup: {
      addressLine1: t.pickupAddress, city: t.pickupCity || bt.pickupCity, state: t.pickupState || bt.pickupState,
      datetime: t.pickupDatetime, airportCode: bt.pickupAirportCode,
      airline: bt.pickupAirline, flightNumber: bt.pickupFlightNumber, terminal: bt.pickupTerminal, flightTime: bt.pickupFlightTime,
    },
    dropoff: {
      addressLine1: t.dropoffAddress, city: t.dropoffCity || bt.dropoffCity, state: t.dropoffState || bt.dropoffState,
      airportCode: bt.dropoffAirportCode,
      airline: bt.dropoffAirline, flightNumber: bt.dropoffFlightNumber, terminal: bt.dropoffTerminal, flightTime: bt.dropoffFlightTime,
    },
    travelers: t.bookings?.[0] ? { numTravelers: bt.numTravelers, numBags: bt.numBags } : null,
    estimatedAmount: bt.estimatedAmount,
    notes: t.notes || bt.additionalComments,
    tripType: t.tripType,
    vehicleName: t.vehicleName,
    bookedSeats: t.bookedSeats,
    totalCapacity: t.totalCapacity,
    startedAt: t.startedAt,
    completedAt: t.completedAt,
  };
}

function formatTime(dt) {
  if (!dt) return null;
  const d = new Date(dt);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function isToday(dt) {
  if (!dt) return false;
  const d = new Date(dt);
  return !isNaN(d.getTime()) && d.toDateString() === new Date().toDateString();
}

function shortLocation(loc) {
  if (!loc) return '—';
  if (loc.airportCode) return `${loc.city || loc.airportCode} (${loc.airportCode})`;
  const parts = [loc.city, loc.state].filter(Boolean);
  return parts.join(', ') || loc.addressLine1 || '—';
}

function fullAddress(loc) {
  if (!loc) return '';
  if (loc.airportCode) {
    return `${loc.airportCode} Airport${loc.terminal ? ` Terminal ${loc.terminal}` : ''}`;
  }
  return [loc.addressLine1, loc.city, loc.state].filter(Boolean).join(', ');
}

function navigateUrl(loc) {
  const q = fullAddress(loc);
  if (!q) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`;
}

function TripCard({ trip, onStatusUpdate, onDecline, blockStart }) {
  const [updating, setUpdating] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showComplete, setShowComplete] = useState(false); // extra-charge prompt on completion
  const [extraAmount, setExtraAmount] = useState('');
  const [extraReason, setExtraReason] = useState('');
  const status = (trip.assignmentStatus || '').toUpperCase();
  const action = STATUS_FLOW[status];
  const offered = status === 'ASSIGNED';   // request awaiting the driver's Accept/Decline
  const inProgress = ACTIVE_STATUSES.includes(status);
  const done = status === 'COMPLETED';
  const id = trip.bookingNumber || (trip.bookingId ? `B-${trip.bookingId}` : '—');
  // Can't start a second trip while another is already underway.
  const startBlocked = status === 'CONFIRMED' && blockStart;

  const pickupNavUrl = navigateUrl(trip.pickup);
  const dropoffNavUrl = navigateUrl(trip.dropoff);
  // Pre-start: head to the pickup. Once underway: head to the dropoff.
  const navTarget = inProgress ? dropoffNavUrl : pickupNavUrl;

  const handleAction = async () => {
    if (!action || updating) return;
    // Completing → first prompt for an optional extra charge (wait time, tip, …).
    if (action.next === 'COMPLETED') { setShowComplete(true); return; }
    setUpdating(true);
    try {
      await onStatusUpdate(trip.assignmentId, action.next);
    } finally {
      setUpdating(false);
    }
  };

  const handleComplete = async () => {
    if (updating) return;
    setUpdating(true);
    try {
      const amt = parseFloat(extraAmount);
      const extra = amt > 0 ? { extraChargeAmount: amt, extraChargeReason: extraReason.trim() } : null;
      await onStatusUpdate(trip.assignmentId, 'COMPLETED', extra);
      setShowComplete(false);
    } finally {
      setUpdating(false);
    }
  };

  const handleAccept = async () => {
    if (updating) return;
    setUpdating(true);
    try {
      await onStatusUpdate(trip.assignmentId, 'CONFIRMED');
    } finally {
      setUpdating(false);
    }
  };

  const handleDecline = async () => {
    if (updating) return;
    setUpdating(true);
    try {
      await onDecline(trip.assignmentId);
    } finally {
      setUpdating(false);
    }
  };

  const cardTone = done
    ? 'border-slate-200 bg-slate-50/60 opacity-80'
    : inProgress
    ? 'border-amber-200 bg-gradient-to-br from-amber-50/50 to-white'
    : offered
    ? 'border-sky-200 bg-gradient-to-br from-sky-50/60 to-white'
    : 'border-slate-200 bg-white';

  return (
    <Card className={cx('p-4', cardTone)}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <StatusBadge status={status} />
          {inProgress && (
            <span className="inline-flex items-center gap-1 text-[11px] text-amber-700">
              <span className="relative flex items-center justify-center w-2 h-2">
                <span className="absolute w-3 h-3 rounded-full bg-amber-400/40 animate-ping" />
                <span className="relative w-2 h-2 rounded-full bg-amber-500" />
              </span>
              Live
            </span>
          )}
          {offered && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700">
              New request
            </span>
          )}
          {done && <IconCircleCheck size={14} className="text-emerald-500" />}
        </div>
        <span className="mono text-[11px] text-slate-400">{id}</span>
      </div>

      <div className={cx('text-[15px] font-bold leading-snug', done ? 'text-slate-600' : 'text-slate-900')}>
        {shortLocation(trip.pickup)} <span className="text-slate-300 font-normal">→</span> {shortLocation(trip.dropoff)}
      </div>

      {trip.customerName && (
        <div className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-slate-700">
          <IconUsers size={13} className="text-slate-400" /> {trip.customerName}
        </div>
      )}

      <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
        {formatTime(trip.pickup?.datetime) && (
          <span className="inline-flex items-center gap-1">
            <IconClock size={12} /> {formatTime(trip.pickup.datetime)}
          </span>
        )}
        {trip.travelers?.numTravelers != null && (
          <span className="inline-flex items-center gap-1">
            <IconUsers size={12} /> {trip.travelers.numTravelers} pax
          </span>
        )}
        {trip.distanceMiles != null && (
          <span>{Number(trip.distanceMiles).toFixed(1)} mi</span>
        )}
        <button
          type="button"
          onClick={() => setShowDetails(true)}
          className="ml-auto text-[12px] font-medium text-sky-700 hover:underline"
        >
          Trip details
        </button>
      </div>

      {/* Shared/Charter trip info */}
      {trip.tripType && trip.tripType !== 'PRIVATE' && (
        <div className="driver-trip-shared">
          <div className="driver-trip-shared-badge">{trip.tripType === 'SHARED' ? 'Shared Ride' : 'Charter'}</div>
          <div className="driver-trip-shared-capacity">{trip.bookedSeats}/{trip.totalCapacity} seats</div>
          {trip.vehicleName && <div className="driver-trip-shared-vehicle">{trip.vehicleName}</div>}
        </div>
      )}

      {trip.notes && (
        <div className="driver-trip-notes">{trip.notes}</div>
      )}

      {offered && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="md"
            onClick={handleDecline}
            disabled={updating}
          >
            {updating ? 'Updating…' : 'Decline'}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleAccept}
            disabled={updating}
          >
            <IconCircleCheck size={14} />
            {updating ? 'Updating…' : 'Accept'}
          </Button>
        </div>
      )}

      {action && (
        <>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="md"
              onClick={() => {
                if (navTarget) window.open(navTarget, '_blank', 'noopener');
              }}
              disabled={!navTarget}
            >
              <IconNavigate size={14} /> Navigate
            </Button>
            <Button
              variant={action.variant}
              size="md"
              onClick={handleAction}
              disabled={updating || startBlocked}
            >
              {action.variant === 'emerald' && <IconCircleCheck size={14} />}
              {updating ? 'Updating…' : action.label}
            </Button>
          </div>
          {startBlocked && (
            <div className="mt-2 text-[12px] text-amber-700">
              Finish your active trip before starting another.
            </div>
          )}
        </>
      )}

      <Modal open={showDetails} onClose={() => setShowDetails(false)} title="Trip details" size="md">
        <div className="space-y-4">
          {(trip.customerName || trip.customerPhone) && (
            <div className="text-sm">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Passenger</div>
              {trip.customerName && <div className="font-medium text-slate-900">{trip.customerName}</div>}
              {trip.customerPhone && (
                <a href={`tel:${trip.customerPhone}`} className="text-sky-700 font-medium hover:underline">{trip.customerPhone}</a>
              )}
            </div>
          )}
          <TripLegDetails label="Pickup" loc={trip.pickup} showTime />
          <div className="border-t border-slate-200" />
          <TripLegDetails label="Drop-off" loc={trip.dropoff} />
          <div className="border-t border-slate-200" />
          <div className="grid grid-cols-2 gap-3 text-sm">
            {trip.travelers?.numTravelers != null && (
              <div><div className="text-xs text-slate-500">Passengers</div><div>{trip.travelers.numTravelers}</div></div>
            )}
            {trip.travelers?.numBags != null && (
              <div><div className="text-xs text-slate-500">Bags</div><div>{trip.travelers.numBags}</div></div>
            )}
          </div>
          {trip.notes && (
            <div className="text-sm"><div className="text-xs text-slate-500">Notes</div><div>{trip.notes}</div></div>
          )}
        </div>
      </Modal>

      <Modal
        open={showComplete}
        onClose={() => { if (!updating) setShowComplete(false); }}
        title="Complete trip"
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="md" onClick={() => setShowComplete(false)} disabled={updating}>Cancel</Button>
            <Button variant="emerald" size="md" onClick={handleComplete} disabled={updating}>
              <IconCircleCheck size={14} />{updating ? 'Completing…' : 'Complete trip'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="text-sm text-slate-600">Add an extra charge for this trip? Leave blank if none.</div>
          <Field label="Extra charge ($)">
            <TextInput type="number" min="0" step="0.01" inputMode="decimal" placeholder="0.00"
              value={extraAmount} onChange={(e) => setExtraAmount(e.target.value)} />
          </Field>
          <Field label="Reason" hint="e.g. wait time (flight delay), tip, extra stop">
            <TextInput value={extraReason} onChange={(e) => setExtraReason(e.target.value)} placeholder="Reason for the charge" />
          </Field>
        </div>
      </Modal>
    </Card>
  );
}

// One leg's full details for the driver popup: address + (if a flight is attached) airline,
// flight number, terminal, and flight time.
function TripLegDetails({ label, loc, showTime }) {
  loc = loc || {};
  const hasFlight = loc.airline || loc.flightNumber || loc.terminal || loc.flightTime;
  return (
    <div className="text-sm">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">{label}</div>
      <div className="font-medium text-slate-900">{fullAddress(loc) || shortLocation(loc)}</div>
      {showTime && formatTime(loc.datetime) && (
        <div className="text-slate-600 mt-0.5">Pickup time: {formatTime(loc.datetime)}</div>
      )}
      {hasFlight && (
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 space-y-0.5">
          <div className="text-xs font-semibold text-slate-500">Flight</div>
          {loc.airline && <div>{loc.airline}{loc.flightNumber ? ` · ${loc.flightNumber}` : ''}</div>}
          {!loc.airline && loc.flightNumber && <div>{loc.flightNumber}</div>}
          {loc.terminal && <div className="text-slate-600">Terminal {loc.terminal}</div>}
          {formatTime(loc.flightTime) && <div className="text-slate-600">Flight time: {formatTime(loc.flightTime)}</div>}
        </div>
      )}
    </div>
  );
}

function MyTripsPage({ authSession }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tracking, setTracking] = useState(false);       // is location streaming active this session?
  const [locPromptDismissed, setLocPromptDismissed] = useState(false); // "Not now" for this session

  const userId = authSession?.user?.sub;

  const getToken = useCallback(async () => {
    let token = authSession?.idToken;
    try {
      const freshSession = await getCurrentSession();
      token = freshSession.idToken;
    } catch (e) { /* use existing */ }
    return token;
  }, [authSession]);

  const fetchTrips = useCallback(async ({ silent } = {}) => {
    if (!userId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Single source of truth: the Trip model (same one the dispatch board assigns
      // through), so board-assigned trips reliably show up here.
      const res = await fetch(`${API_BASE}/api/trips/by-driver-user/${userId}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const tripData = await res.json();
      setTrips(tripData.map(normalizeTrip));
    } catch (err) {
      console.error('Failed to fetch trips:', err);
      if (!silent) setError('Failed to load your trips'); // keep last-good data on a transient poll blip
    } finally {
      if (!silent) setLoading(false);
    }
  }, [userId, getToken]);

  // Stream GPS to the backend (driver opts in via the banner, or auto on EN_ROUTE).
  const beginTracking = useCallback(async () => {
    if (!userId) return;
    try {
      const token = await getToken();
      if (token) setAuthToken(token);
      await startTracking({
        driverId: userId,
        onLocation: (fix) => console.log('[trip-tracking] fix', fix.latitude.toFixed(5), fix.longitude.toFixed(5)),
        onError: (err) => console.warn('[trip-tracking] error', err.message || err),
      });
      setTracking(true);
    } catch (e) {
      console.warn('[trip-tracking] failed to start:', e.message);
    }
  }, [userId, getToken]);

  const handleStatusUpdate = async (assignmentId, newStatus, extra) => {
    try {
      const token = await getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // On completion, optionally carry a driver-entered extra charge into the accounting email.
      const res = await fetch(`${API_BASE}/api/trips/${assignmentId}/status?status=${newStatus}`, {
        method: 'PATCH', headers,
        body: extra ? JSON.stringify(extra) : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();

      // Start trip → begin GPS tracking. Complete → stop it.
      if (newStatus === 'EN_ROUTE' && userId) {
        await beginTracking();
      }
      if (newStatus === 'COMPLETED') {
        try {
          await stopTracking();
          setTracking(false);
          console.log('[trip-tracking] stopped for trip', assignmentId);
        } catch (e) {
          console.warn('[trip-tracking] failed to stop:', e.message);
        }
      }

      // Re-normalize the returned TripResponse so the card keeps its shape (status/fields).
      setTrips((prev) => prev.map((t) =>
        t.assignmentId === assignmentId ? normalizeTrip(updated) : t
      ));
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update trip status');
    }
  };

  // Decline a request → clear the driver off the trip (back to the dispatcher's queue).
  // It leaves the driver's list, so drop it locally.
  const handleDecline = async (assignmentId) => {
    try {
      const token = await getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/api/trips/${assignmentId}/unassign-driver`, {
        method: 'PATCH', headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTrips((prev) => prev.filter((t) => t.assignmentId !== assignmentId));
    } catch (err) {
      console.error('Failed to decline trip:', err);
      alert('Failed to decline trip');
    }
  };

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  // Poll so a freshly-assigned trip (and its share-location prompt) appears without a refresh.
  useEffect(() => {
    if (!userId) return undefined;
    const id = setInterval(() => fetchTrips({ silent: true }), 45000);
    return () => clearInterval(id);
  }, [userId, fetchTrips]);

  const today = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  }, []);

  const count = trips.length;

  // A driver can only have one trip underway at a time — used to lock "Start trip" on the
  // other (CONFIRMED) cards while one is active. The backend also enforces this (409).
  const hasActiveTrip = useMemo(
    () => trips.some(t => ACTIVE_STATUSES.includes((t.assignmentStatus || '').toUpperCase())),
    [trips],
  );

  // Prompt to share location when there's a today trip the driver has ACCEPTED (CONFIRMED)
  // but not started, and GPS isn't already streaming. A still-pending request (ASSIGNED)
  // doesn't prompt — and EN_ROUTE auto-starts tracking.
  const locationTrip = useMemo(
    () => trips.find(t => (t.assignmentStatus || '').toUpperCase() === 'CONFIRMED'
      && isToday(t.pickup?.datetime)),
    [trips],
  );
  const showLocationPrompt = !!locationTrip && !tracking && !locPromptDismissed;

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50">
      <div className="px-6 py-6 max-w-3xl mx-auto space-y-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Today's trips</h1>
            <p className="text-sm text-slate-500 mt-1">{today}</p>
          </div>
          <span className="text-xs font-medium text-slate-500 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-full">
            {count} trip{count === 1 ? '' : 's'}
          </span>
        </div>

        {showLocationPrompt && (
          <Card className="p-4 border-amber-200 bg-gradient-to-br from-amber-50/70 to-white">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <IconNavigate size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">
                  You've got a trip today{formatTime(locationTrip.pickup?.datetime) ? ` at ${formatTime(locationTrip.pickup.datetime)}` : ''}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Share your location so dispatch can see you on the map.
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button variant="primary" size="md" onClick={beginTracking}>
                    <IconNavigate size={14} /> Share location
                  </Button>
                  <Button variant="ghost" size="md" onClick={() => setLocPromptDismissed(true)}>Not now</Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {tracking && (
          <div className="flex items-center gap-2 text-[12px] text-emerald-700">
            <span className="relative flex items-center justify-center w-2 h-2">
              <span className="absolute w-3 h-3 rounded-full bg-emerald-400/40 animate-ping" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            Sharing your location with dispatch
          </div>
        )}

        {loading ? (
          <Card className="p-10 text-center text-sm text-slate-500">Loading your trips…</Card>
        ) : error ? (
          <Card className="p-6 text-center">
            <div className="text-sm text-rose-700 mb-3">{error}</div>
            <Button variant="outline" size="sm" onClick={fetchTrips}>Retry</Button>
          </Card>
        ) : trips.length === 0 ? (
          <Card className="p-10 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3">
              <IconNavigate size={20} />
            </div>
            <div className="text-sm font-semibold text-slate-800">No trips assigned</div>
            <div className="text-xs text-slate-500 mt-1">Your assigned trips will appear here.</div>
          </Card>
        ) : (
          <div className="space-y-3">
            {trips.map((trip) => (
              <TripCard
                key={trip.assignmentId}
                trip={trip}
                onStatusUpdate={handleStatusUpdate}
                onDecline={handleDecline}
                blockStart={hasActiveTrip && !ACTIVE_STATUSES.includes((trip.assignmentStatus || '').toUpperCase())}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyTripsPage;
